/**
 * Firestore Batch Utilities
 * 
 * Handles Firestore's 500 operation limit per batch by automatically
 * splitting into multiple batches when needed.
 */

import type { Firestore, WriteBatch, DocumentReference } from 'firebase-admin/firestore'
import { createLogger } from './logger.js'

const logger = createLogger('FirestoreBatch')

/** Maximum operations per Firestore batch */
const FIRESTORE_BATCH_LIMIT = 500

/** Soft delete marker field */
export const SOFT_DELETE_FIELD = 'deletedAt'

export interface BatchOperation {
  type: 'delete' | 'softDelete' | 'set' | 'update'
  ref: DocumentReference
  data?: Record<string, unknown>
}

export interface BatchResult {
  totalOperations: number
  batchesCommitted: number
  success: boolean
  errors: Error[]
}

/**
 * Smart batch that handles Firestore's 500 operation limit.
 * Automatically commits and creates new batches when needed.
 */
export class SmartBatch {
  private readonly firestore: Firestore
  private currentBatch: WriteBatch
  private operationCount = 0
  private totalOperations = 0
  private batchesCommitted = 0
  private errors: Error[] = []

  constructor(firestore: Firestore) {
    this.firestore = firestore
    this.currentBatch = firestore.batch()
  }

  /**
   * Delete a document (hard delete)
   */
  delete(ref: DocumentReference): this {
    this.ensureBatchCapacity()
    this.currentBatch.delete(ref)
    this.operationCount++
    this.totalOperations++
    return this
  }

  /**
   * Soft delete a document (marks as deleted, keeps data)
   */
  softDelete(ref: DocumentReference): this {
    this.ensureBatchCapacity()
    this.currentBatch.update(ref, {
      [SOFT_DELETE_FIELD]: new Date(),
      updatedAt: new Date()
    })
    this.operationCount++
    this.totalOperations++
    return this
  }

  /**
   * Set a document
   */
  set(ref: DocumentReference, data: Record<string, unknown>, options?: { merge?: boolean }): this {
    this.ensureBatchCapacity()
    this.currentBatch.set(ref, data, options || {})
    this.operationCount++
    this.totalOperations++
    return this
  }

  /**
   * Update a document
   */
  update(ref: DocumentReference, data: Record<string, unknown>): this {
    this.ensureBatchCapacity()
    this.currentBatch.update(ref, data)
    this.operationCount++
    this.totalOperations++
    return this
  }

  /**
   * Ensure batch has capacity, commit and create new if at limit
   */
  private async ensureBatchCapacity(): Promise<void> {
    if (this.operationCount >= FIRESTORE_BATCH_LIMIT) {
      await this.commitCurrentBatch()
      this.currentBatch = this.firestore.batch()
      this.operationCount = 0
    }
  }

  /**
   * Commit current batch
   */
  private async commitCurrentBatch(): Promise<void> {
    if (this.operationCount > 0) {
      try {
        await this.currentBatch.commit()
        this.batchesCommitted++
        logger.debug('Batch committed', { 
          operations: this.operationCount, 
          batchNumber: this.batchesCommitted 
        })
      } catch (error) {
        this.errors.push(error as Error)
        logger.error('Batch commit failed', { 
          error: (error as Error).message,
          batchNumber: this.batchesCommitted + 1 
        })
        throw error
      }
    }
  }

  /**
   * Commit all pending operations
   */
  async commit(): Promise<BatchResult> {
    await this.commitCurrentBatch()
    
    const result: BatchResult = {
      totalOperations: this.totalOperations,
      batchesCommitted: this.batchesCommitted,
      success: this.errors.length === 0,
      errors: this.errors
    }

    logger.info('SmartBatch completed', {
      totalOperations: result.totalOperations,
      batchesCommitted: result.batchesCommitted,
      success: result.success
    })

    return result
  }

  /**
   * Get current stats
   */
  getStats(): { pending: number; total: number; batches: number } {
    return {
      pending: this.operationCount,
      total: this.totalOperations,
      batches: this.batchesCommitted
    }
  }
}

/**
 * Execute multiple delete operations with retry logic.
 * Useful for cleaning up related documents.
 */
export async function batchDeleteWithRetry(
  firestore: Firestore,
  refs: DocumentReference[],
  options: { 
    maxRetries?: number
    softDelete?: boolean 
  } = {}
): Promise<BatchResult> {
  const { maxRetries = 3, softDelete = false } = options
  
  let lastError: Error | null = null
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const batch = new SmartBatch(firestore)
      
      for (const ref of refs) {
        if (softDelete) {
          batch.softDelete(ref)
        } else {
          batch.delete(ref)
        }
      }
      
      return await batch.commit()
    } catch (error) {
      lastError = error as Error
      logger.warn(`Batch delete attempt ${attempt}/${maxRetries} failed`, {
        error: lastError.message,
        refsCount: refs.length
      })
      
      if (attempt < maxRetries) {
        // Exponential backoff: 100ms, 200ms, 400ms...
        await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt - 1)))
      }
    }
  }
  
  throw lastError || new Error('Batch delete failed after all retries')
}

/**
 * Helper to collect all document refs from multiple queries
 */
export async function collectRefsFromQueries(
  queries: Promise<FirebaseFirestore.QuerySnapshot>[]
): Promise<DocumentReference[]> {
  const snapshots = await Promise.all(queries)
  const refs: DocumentReference[] = []

  for (const snapshot of snapshots) {
    for (const doc of snapshot.docs) {
      refs.push(doc.ref)
    }
  }

  return refs
}

/**
 * Fluent batch builder that uses collection paths instead of DocumentReferences.
 * Simplifies batch operations by handling ref creation internally.
 *
 * @example
 * ```typescript
 * const builder = new FirestoreBatchBuilder(firestore)
 * const stageId = builder.addSet('stages', null, stageData) // auto-generate ID
 * builder
 *   .addSet('stages', 'existing-id', updatedData)
 *   .addUpdate('plans', planId, { updatedAt: new Date() })
 *   .addDelete('stages', oldStageId)
 *
 * await builder.commit()
 * ```
 */
export class FirestoreBatchBuilder {
  private readonly firestore: Firestore
  private readonly smartBatch: SmartBatch
  private readonly generatedIds: Map<string, string> = new Map()

  constructor(firestore: Firestore) {
    this.firestore = firestore
    this.smartBatch = new SmartBatch(firestore)
  }

  /**
   * Set a document in a collection.
   * @param collection - Collection path (e.g., 'plans', 'stages')
   * @param docId - Document ID, or null to auto-generate
   * @param data - Document data
   * @param options - Set options (merge)
   * @returns The document ID (useful when auto-generating)
   */
  addSet(
    collection: string,
    docId: string | null,
    data: Record<string, unknown>,
    options?: { merge?: boolean }
  ): string {
    const collectionRef = this.firestore.collection(collection)
    const docRef = docId ? collectionRef.doc(docId) : collectionRef.doc()
    this.smartBatch.set(docRef, data, options)
    return docRef.id
  }

  /**
   * Update a document in a collection.
   * @param collection - Collection path
   * @param docId - Document ID
   * @param data - Fields to update
   */
  addUpdate(collection: string, docId: string, data: Record<string, unknown>): this {
    const ref = this.firestore.collection(collection).doc(docId)
    this.smartBatch.update(ref, data)
    return this
  }

  /**
   * Delete a document from a collection.
   * @param collection - Collection path
   * @param docId - Document ID
   */
  addDelete(collection: string, docId: string): this {
    const ref = this.firestore.collection(collection).doc(docId)
    this.smartBatch.delete(ref)
    return this
  }

  /**
   * Soft delete a document (marks as deleted, keeps data).
   * @param collection - Collection path
   * @param docId - Document ID
   */
  addSoftDelete(collection: string, docId: string): this {
    const ref = this.firestore.collection(collection).doc(docId)
    this.smartBatch.softDelete(ref)
    return this
  }

  /**
   * Delete a document using its DocumentReference directly.
   * Useful when iterating over query results.
   */
  deleteRef(ref: DocumentReference): this {
    this.smartBatch.delete(ref)
    return this
  }

  /**
   * Update a document using its DocumentReference directly.
   * Useful when iterating over query results.
   */
  updateRef(ref: DocumentReference, data: Record<string, unknown>): this {
    this.smartBatch.update(ref, data)
    return this
  }

  /**
   * Set a document using its DocumentReference directly.
   */
  setRef(ref: DocumentReference, data: Record<string, unknown>, options?: { merge?: boolean }): this {
    this.smartBatch.set(ref, data, options)
    return this
  }

  /**
   * Touch a document's updatedAt field.
   * Common pattern for maintaining parent document timestamps.
   * @param collection - Collection path
   * @param docId - Document ID
   * @param timestamp - Optional timestamp (defaults to now)
   */
  touchUpdatedAt(collection: string, docId: string, timestamp?: Date): this {
    const ref = this.firestore.collection(collection).doc(docId)
    this.smartBatch.update(ref, { updatedAt: timestamp ?? new Date() })
    return this
  }

  /**
   * Get current operation stats.
   */
  getStats(): { pending: number; total: number; batches: number } {
    return this.smartBatch.getStats()
  }

  /**
   * Commit all pending operations.
   */
  async commit(): Promise<BatchResult> {
    return this.smartBatch.commit()
  }
}

/**
 * Factory function for creating a batch builder.
 * @example
 * ```typescript
 * const batch = createBatchBuilder(this.firestore)
 * batch.addSet('stages', null, stageData)
 *      .touchUpdatedAt('plans', planId)
 * await batch.commit()
 * ```
 */
export function createBatchBuilder(firestore: Firestore): FirestoreBatchBuilder {
  return new FirestoreBatchBuilder(firestore)
}
